import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getMultiSelectTreeValue } from "@progress/kendo-react-dropdowns";
import {
  processMultiSelectTreeData,
  expandedState,
  fields,
  onChangeTreeData,
} from "../kendoComponents/multiSelectTreeUtils/shared-dd-multiselecttree-data-operations";
import { useEffectOnChange } from "./CustomHooks";
import { toogleDataCheck } from "../../store/app/DataQualityStatus/DataQualitySlice";


/**
 * @typedef {Object} UseMultiSelectTreeParams
 * @property {Array} rawData - Raw data to transform into tree structure
 * @property {Array} stateValue - Current selected values from Redux state
 * @property {string} urlParamKey - URL query parameter key to read initial value from
 * @property {Array} [defaultValue=[]] - Fallback value if URL param is missing or invalid
 * @property {function} transformData - Function to transform rawData into tree-compatible format
 * @property {function} setFilterAction - Redux action to dispatch on selection change
 * @property {boolean} [expandParentOnInit=false] - Whether to expand parent nodes on first load
 * @property {string} [selectionLabel="items"] - Label used in the tags when multiple items are selected
 */

/**
 * @param {UseMultiSelectTreeParams} params
 */
const useMultiSelectTree = ({
  rawData,
  stateValue,
  urlParamKey,
  defaultValue = [],
  transformData,
  setFilterAction,
  expandParentOnInit = false,
  selectionLabel = "items",  
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const refreshCount = useSelector(
    (state) => state.dataQualityReducer.refreshCount
  );

  const [value, setValue] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const transformedData = useMemo(
    () => transformData(rawData),
    [rawData, transformData]
  );

  const treeData = useMemo(
    () => processMultiSelectTreeData(transformedData, { expanded, value, ...fields }),
    [expanded, transformedData, value]
  );

  // Init from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    let urlValue;
    try {
      urlValue = JSON.parse(searchParams.get(urlParamKey)) || [];
    } catch (error) {
      urlValue = defaultValue;
      console.error(error);
    }
    dispatch(setFilterAction(urlValue));
  }, [navigate, refreshCount]);

  // Count selected leaf nodes
  useEffectOnChange(() => {
    let count = 0;
    value.forEach((item) => {
      if (item.hasOwnProperty("items") || typeof item.id === "number") {
        count++;
      }
    });
    setSelected(value.length - count);
  }, [value]);

  // First load: sync tree with state
  useEffectOnChange(() => {
    if (transformedData.length > 0 && !isReady) {
      let newStateValue = stateValue;

      if (expandParentOnInit) {
        let expanded = [];
        stateValue.forEach((item) => {
          if (typeof item === "number") {
            const category = transformedData.find((c) => c.id === item);
            if (category) {
              category.items.forEach((subItem) => expanded.push(subItem.id));
            }
          }
        });
        newStateValue = [...new Set([...stateValue, ...expanded])];
        dispatch(setFilterAction(newStateValue));
      }

      setValue(onChangeTreeData(treeData, newStateValue));
      setIsReady(true);
    }
  }, [transformedData, isReady]);

  // Sync tree when stateValue changes
  useEffect(() => {
    if (transformedData.length > 0 && isReady) {
      setValue(onChangeTreeData(treeData, stateValue));
    }
  }, [stateValue, isReady]);

  const onChangeTree = (event) => {
    dispatch(toogleDataCheck(true));

    if (event.operation === "clear") {
      dispatch(setFilterAction([]));
      setValue([]);
      return;
    }

    if (event.operation === "toggle") {
      const clickedItem = event.items[0];
      const isParent = clickedItem.items && clickedItem.items.length > 0;

      if (isParent) {
        const childIds = clickedItem.items.map((child) => child.id);
        const allChildrenSelected = childIds.every((id) => stateValue.includes(id));
        const newStateValue = allChildrenSelected
          ? stateValue.filter((id) => !childIds.includes(id))
          : [...new Set([...stateValue, ...childIds])];
        dispatch(setFilterAction(newStateValue));
      } else {
        const itemId = clickedItem.id;
        const newStateValue = stateValue.includes(itemId)
          ? stateValue.filter((id) => id !== itemId)
          : [...stateValue, itemId];
        dispatch(setFilterAction(newStateValue));
      }
    }

    setValue(getMultiSelectTreeValue(transformedData, { ...fields, ...event, value }));
  };

  const onExpandChange = useCallback(
    (event) => setExpanded(expandedState(event.item, fields.dataItemKey, expanded)),
    [expanded]
  );

  
  const getTags = () => {
    if (selected === 0) return [{ text: "All", data: [...value] }];
    if (selected === 1 && value.length === 1) {
      return [{ text: value[0].text || value[0].id || "1 selected", data: [...value] }];
    }
    return [{ text: `${selected} ${selectionLabel} selected`, data: [...value] }];
  };


    return { treeData, value, selected, onChangeTree, onExpandChange, fields, getTags };

};

export default useMultiSelectTree;